import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ArrowUpRight, Clock, Users, Zap, Shield, Target, Heart, Lightbulb, Rocket, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Golden Circle - Simon Sinek inspired (Why → How → What)
const goldenCircle = [
  {
    icon: Heart,
    label: "Why",
    title: "Why We Exist",
    description: "Because we've carried the weight of missed payrolls, sleepless nights, and lessons learned too late. We exist to help founders avoid the blind risks we once took.",
    link: "/why-we-exist",
  },
  {
    icon: Lightbulb,
    label: "How",
    title: "How We Operate",
    description: "Validate before building. Build what matters. Get real paying customers. Make it seed-funding ready. Then handover or scale together.",
    link: "/how-we-help",
  },
  {
    icon: Rocket,
    label: "What",
    title: "What We Deliver",
    description: "A validated product with paying customers, ready for seed funding. We build it, market it, and get your first customers. You decide: take it forward or let us help you scale.",
    link: "/what-we-do",
  },
];

// SECTION: What We Do (Concrete)
const whatWeDo = [
  {
    title: "Validate & Build",
    description: "Customer interviews, demand testing, and MVP development. We build your product, not just advise.",
    timeframe: "3-6 weeks",
  },
  {
    title: "Get Paying Customers",
    description: "Marketing and sales to acquire your first paying customers. Real traction, not vanity metrics.",
    timeframe: "Post-Launch",
  },
  {
    title: "Handover or Scale",
    description: "Receive a seed-funding-ready product with traction. Or continue with us for scaling support.",
    timeframe: "Your Choice",
  },
];

// SECTION: Who It's For (Situational)
const whoItsFor = [
  {
    icon: Users,
    situation: "You have an idea but haven't validated demand yet",
    stage: "Pre-MVP",
  },
  {
    icon: Zap,
    situation: "You've built something but aren't sure if it's the right thing",
    stage: "Early Stage",
  },
  {
    icon: Shield,
    situation: "You have traction but need help scaling without burning cash",
    stage: "Growth",
  },
];

// Custom illustrations
const illustrations = {
  hero: "/images/hero.png",
  teamwork: "/images/teamwork.png",
};

// How We Deliver (Process steps)
const howWeDeliver = [
  {
    title: "Week 1-2",
    description: "Customer interviews, demand validation, pricing strategy. We confirm there's a market before building.",
  },
  {
    title: "Week 3-6",
    description: "We build your MVP. Real product, real users, real feedback. You focus on vision while we handle execution.",
  },
  {
    title: "Post-Launch",
    description: "Marketing and sales to get your first paying customers. Real revenue, real traction.",
  },
  {
    title: "Handover or Scale",
    description: "Your product is seed-funding ready. Take it forward yourself, or continue with us to scale.",
  },
];

export default function HomePage() {
  return (
    <div className="flex flex-col">
      {/* ============================================
          HERO SECTION - What We Do (Concrete)
          Must answer in 5-7 seconds:
          1. What does this company help me do?
          2. Is this meant for someone like me?
          3. How soon does this help me?
          4. What should I do next?
      ============================================ */}
      <section className="bg-hero noise-overlay relative overflow-hidden">
        <div className="container-wide py-16 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="initial"
              animate="animate"
              variants={stagger}
              className="space-y-6"
            >
              {/* Concrete Statement - What We Do */}
              <motion.h1 variants={fadeUp} className="heading-display text-foreground">
                We build your product, get your first paying customers, and make it seed-funding ready.
              </motion.h1>
              
              {/* Time-to-Value + Who It's For */}
              <motion.p variants={fadeUp} className="body-large text-muted-foreground max-w-xl">
                <span className="text-foreground font-medium">From idea to paying customers in weeks, not months.</span>{" "}
                Then choose: take over a validated product, or let us help you scale.
              </motion.p>
              
              {/* Clear Primary CTA */}
              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 pt-2">
                <Button variant="accent" size="lg" asChild className="rounded-full">
                  <Link to="/contact" data-testid="hero-cta-primary">
                    Tell Us What You're Building
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild className="rounded-full">
                  <Link to="/process" data-testid="hero-cta-secondary">
                    See How It Works
                  </Link>
                </Button>
              </motion.div>

              {/* Quick Trust Signals - Not Philosophy */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-4 pt-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-accent" />
                  We build it
                </span>
                <span className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-accent" />
                  We get customers
                </span>
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-accent" />
                  Seed-funding ready
                </span>
              </motion.div>
            </motion.div>

            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:flex items-center justify-center"
            >
              <div className="w-full max-w-lg">
                <img
                  src={illustrations.hero}
                  alt="From idea to validated product"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================================
          GOLDEN CIRCLE SECTION - Simon Sinek Inspired
          Why → How → What
      ============================================ */}
      <section className="bg-background py-16 sm:py-20 lg:py-24 border-b border-border/30">
        <div className="container-wide">
          {/* Golden Circle Illustration + Header */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex justify-center"
            >
              <div className="w-full max-w-md">
                <img
                  src="/images/golden-circle.png"
                  alt="The Golden Circle - Why, How, What - Start with Why"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="heading-large text-foreground mb-4">The Why, How, and What</h2>
              <p className="body-base text-muted-foreground mb-6">
                We are not an agency. We are not consultants. We are founders who have been where you are, and we learned what actually matters.
              </p>
              <p className="body-base text-muted-foreground mb-6">
                People don't buy <span className="text-foreground font-medium">what</span> you do. They buy <span className="text-foreground font-medium">why</span> you do it. Our "Why" is simple: we've lived the founder journey, and we know what it costs when clarity comes too late.
              </p>
              <p className="text-sm text-muted-foreground italic">
                Inspired by Simon Sinek's{" "}
                <a 
                  href="https://simonsinek.com/golden-circle/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-accent hover:underline font-medium"
                >
                  Golden Circle
                </a>
              </p>
            </motion.div>
          </div>

          {/* Three Pillars */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {goldenCircle.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card className="card-elevated h-full">
                  <CardContent className="p-6 lg:p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <item.icon className="h-5 w-5 text-accent" />
                      </div>
                      <Badge variant="outline" className="text-accent border-accent/30 font-medium">
                        {item.label}
                      </Badge>
                    </div>
                    <h3 className="font-serif text-xl font-medium text-foreground mb-3">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {item.description}
                    </p>
                    <Link
                      to={item.link}
                      className="inline-flex items-center text-sm font-medium text-accent hover:text-accent/80 transition-colors group"
                    >
                      Learn more
                      <ArrowUpRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          SECTION: What We Actually Do (Concrete)
          Concrete outcomes with timelines
      ============================================ */}
      <section className="bg-section py-16 sm:py-20 lg:py-24">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="heading-large text-foreground mb-4">What We Do</h2>
            <p className="body-base text-muted-foreground max-w-2xl mx-auto">
              We don't just advise. We build, market, and deliver paying customers.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {whatWeDo.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card className="card-elevated h-full">
                  <CardContent className="p-6 lg:p-8">
                    <Badge variant="outline" className="mb-4 text-accent border-accent/30">
                      {item.timeframe}
                    </Badge>
                    <h3 className="font-serif text-xl font-medium text-foreground mb-3">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mt-10"
          >
            <Button variant="outline" asChild className="rounded-full">
              <Link to="/what-we-do">
                See Full Service Details
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          SECTION 3: Who It's For (Situational)
          Self-selection based on situation, not labels
      ============================================ */}
      <section className="bg-section py-16 sm:py-20 lg:py-24">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex justify-center order-2 lg:order-1"
            >
              <div className="w-full max-w-md">
                <img
                  src={illustrations.teamwork}
                  alt="Founders we work with"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="order-1 lg:order-2"
            >
              <h2 className="heading-large text-foreground mb-4">Is This For You?</h2>
              <p className="body-base text-muted-foreground mb-8">
                We work with founders in these situations:
              </p>

              <div className="space-y-4">
                {whoItsFor.map((item, i) => (
                  <motion.div
                    key={item.stage}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                    className="flex items-start gap-4 p-4 bg-background rounded-lg border border-border/50"
                  >
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-foreground font-medium mb-1">{item.situation}</p>
                      <Badge variant="secondary" className="text-xs">{item.stage}</Badge>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button variant="accent" asChild className="rounded-full">
                  <Link to="/contact">
                    Yes, This Is Me
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
                <Button variant="ghost" asChild className="rounded-full">
                  <Link to="/not-for-everyone">
                    Not Sure? Check Who This Is NOT For
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================================
          SECTION 4: How We Deliver (Process Overview)
          Clear timeline, concrete steps
      ============================================ */}
      <section className="bg-background py-16 sm:py-20 lg:py-24">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="heading-large text-foreground mb-4">How It Works</h2>
            <p className="body-base text-muted-foreground max-w-2xl mx-auto">
              From validation to paying customers to seed-funding ready. Here's the path.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-px bg-border hidden sm:block" />
              
              {howWeDeliver.map((step, i) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  className="relative flex gap-6 pb-8 last:pb-0"
                >
                  {/* Timeline dot */}
                  <div className="w-12 h-12 rounded-full bg-accent/10 border-2 border-accent flex items-center justify-center flex-shrink-0 z-10">
                    <span className="text-accent font-semibold">{i + 1}</span>
                  </div>
                  
                  {/* Content */}
                  <div className="pt-2">
                    <h3 className="font-serif text-lg font-medium text-foreground mb-1">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center mt-10"
          >
            <Button variant="outline" asChild className="rounded-full">
              <Link to="/process">
                See Detailed Process
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          SECTION 5: Why This Approach (Credibility)
          Moved to AFTER clarity is established
      ============================================ */}
      <section className="bg-section py-16 sm:py-20 lg:py-24">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="heading-large text-foreground mb-4">Why We Work This Way</h2>
            <p className="body-base text-muted-foreground max-w-2xl mx-auto mb-8">
              We've been founders. We've made the mistakes. We've learned what actually matters.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto text-left">
              {[
                "Validate before you build, not after",
                "Revenue proves demand, not opinions",
                "Small bets before big commitments",
                "Clarity reduces risk better than speed",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 + i * 0.05 }}
                  className="flex items-center gap-3 text-foreground"
                >
                  <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0" />
                  <span>{point}</span>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-8"
            >
              <Button variant="outline" asChild className="rounded-full">
                <Link to="/why-we-exist">
                  Read Our Full Story
                  <ArrowUpRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          WAY FORWARD - Our Roadmap
      ============================================ */}
      <section className="bg-background section-padding">
        <div className="container-base">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="heading-large text-foreground mb-4">The Way Forward</h2>
            <p className="body-base text-muted-foreground max-w-2xl mx-auto">
              Our commitment to quality, and what's next.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Quality Commitment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="card-elevated h-full">
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <Users className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="font-serif text-xl font-medium text-foreground mb-3">
                    Limited Founders. By Design.
                  </h3>
                  <p className="text-muted-foreground">
                    We deliberately limit our engagements. Not because we can't scale, but because we won't. Each founder deserves our full attention, not a fraction of it. Quality over volume, always.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Investor Network */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="card-elevated h-full">
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                    <Handshake className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="font-serif text-xl font-medium text-foreground mb-3">
                    Building an Investor Network
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    We're connecting serious investors with our validated, customer-ready products. No middleman games. No promises. Just honest introductions when founders want them.
                  </p>
                  <Button variant="outline" size="sm" asChild className="rounded-full">
                    <Link to="/investors">
                      Learn More
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================================
          FINAL CTA - Clear Next Step
      ============================================ */}
      <section className="bg-primary text-primary-foreground py-16 sm:py-20 lg:py-24">
        <div className="container-narrow text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-large mb-4">Have an idea you're serious about?</h2>
            <p className="body-large text-primary-foreground/80 mb-8 max-w-xl mx-auto">
              Tell us what you're building. We'll tell you honestly if we can help, and how.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                variant="secondary" 
                size="lg" 
                asChild 
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full"
              >
                <Link to="/contact" data-testid="final-cta-primary">
                  Tell Us What You're Building
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                asChild 
                className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full"
              >
                <Link to="/pricing">See Pricing</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
